import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, FormField, Textarea, useToast } from '@yhelp/ui';
import { useAuthorizedFetch } from '@/lib/useAuthorizedFetch';

interface FloatingTicketChatProps {
  ticketId: string;
  isAgent: boolean;
}

export function FloatingTicketChat({ ticketId, isAgent }: FloatingTicketChatProps) {
  const authorizedFetch = useAuthorizedFetch();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState('');
  const [isInternal, setIsInternal] = useState(false);

  const postComment = useMutation({
    mutationFn: () =>
      authorizedFetch(`/v1/tickets/${ticketId}/comments`, {
        method: 'POST',
        body: JSON.stringify({ body, is_internal: isInternal }),
      }),
    onSuccess: () => {
      setBody('');
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['comments', ticketId] });
      toast({ variant: 'success', title: 'Mensagem enviada' });
    },
    onError: () => toast({ variant: 'error', title: 'Erro ao enviar mensagem' }),
  });

  return (
    <>
      <button
        type="button"
        data-tour="floating-chat"
        aria-expanded={open}
        aria-label={open ? 'Fechar chat' : 'Abrir chat do chamado'}
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-4 left-4 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-yhelp-yellow text-yhelp-black shadow-brand transition-transform duration-fast hover:scale-105 focus-visible:outline-yhelp-black motion-reduce:transition-none motion-reduce:hover:scale-100 sm:bottom-6 sm:left-6 animate-pulse-soft motion-reduce:animate-none"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          className="h-7 w-7"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Conversa do chamado"
          className="fixed bottom-20 left-4 z-50 w-[min(22rem,calc(100vw-2rem))] overflow-hidden animate-fade-in-up rounded-2xl border border-neutral-200 bg-white shadow-brand motion-reduce:animate-none dark:border-neutral-700 dark:bg-neutral-900 sm:bottom-24 sm:left-6"
        >
          <div className="flex items-center justify-between bg-gradient-to-r from-yhelp-yellow/30 via-yhelp-yellow/10 to-transparent px-4 py-3 dark:from-yhelp-yellow/20 dark:via-yhelp-yellow/5">
            <h3 className="font-bold text-yhelp-black dark:text-white">Conversa</h3>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-md px-2 py-1 text-sm text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
              aria-label="Fechar conversa"
            >
              Fechar
            </button>
          </div>
          <form
            className="space-y-3 p-4"
            onSubmit={(e) => {
              e.preventDefault();
              postComment.mutate();
            }}
          >
            <FormField id="floating-comment" label="Mensagem">
              <Textarea
                id="floating-comment"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                required
                rows={4}
                placeholder="Digite sua mensagem…"
              />
            </FormField>
            {isAgent ? (
              <label
                className="flex items-center gap-2 text-sm text-yhelp-black dark:text-neutral-200"
                data-tour="ticket-internal-toggle"
              >
                <input
                  type="checkbox"
                  checked={isInternal}
                  onChange={(e) => setIsInternal(e.target.checked)}
                />
                Nota interna (só agentes)
              </label>
            ) : null}
            <Button type="submit" loading={postComment.isPending} className="w-full">
              Enviar
            </Button>
          </form>
        </div>
      ) : null}
    </>
  );
}
